'use client';

import { useTranslations } from 'next-intl';
import { cx } from '@/styled-system/css';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import Card from '@/src/components/features/property/ui/Card';
import { ShowMoreContent } from '@/src/components/features/property/ui/ShowMoreContent';
import {
  formatWholeNumber,
  getPricingComparisonDisplay,
  type PricingComparisonKind,
} from './pricingComparisonModule.utils';
import {
  areaRow,
  areaRowLink,
  areaRowValue,
  averagesCardHeader,
  averagesList,
  colorCheaper,
  colorMore,
  container,
  deltaIcon,
  description,
  inlineLink,
  summaryDelta,
  summaryRow,
} from './pricingComparisonModule.styles';

type Props = {
  listing: PropertyDetailListing;
};

const colorClassForKind = (kind: PricingComparisonKind) => {
  if (kind === 'less') return colorCheaper;
  if (kind === 'more') return colorMore;
  return undefined;
};

const useHomeQualifier = (listingType: string | undefined, beds: number | undefined) => {
  const t = useTranslations('hdp');

  if (listingType === 'room') {
    return [t('homeQualifierRoom'), t('homeQualifierRooms')] as const;
  }
  if (beds === 0) {
    return [t('homeQualifierStudio'), t('homeQualifierStudios')] as const;
  }
  if (typeof beds === 'number') {
    return [t('homeQualifierBedroom', { count: beds }), t('homeQualifierBedrooms', { count: beds })] as const;
  }
  return [t('homeQualifierHome'), t('homeQualifierHomes')] as const;
};

export const PricingComparisonModule = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const display = getPricingComparisonDisplay(listing);
  const [singular, plural] = useHomeQualifier(listing.listingType, display?.beds);

  if (!display) return null;

  const colorClass = colorClassForKind(display.kind);
  const phrase = t(`pricingComparisonPhrase_${display.kind}`);
  const areaName = display.previewStat.name;
  const areaHref = display.previewStat.link;
  const preview = (
    <div className={container}>
      {display.kind !== 'equal' && (
        <div className={summaryRow}>
          <LucideIcon
            icon={display.kind === 'less' ? TrendingDown : TrendingUp}
            size="sm"
            className={cx(deltaIcon, colorClass)}
          />
          <span className={cx(summaryDelta, colorClass)}>{display.previewStat.difference}</span>
        </div>
      )}

      <p className={description}>
        {t('pricingComparisonIntro', {
          price: display.listingPriceDisplay,
          phrase,
          home: singular,
          difference: display.kind === 'equal' ? '' : display.previewStat.difference,
        })}{' '}
        <a className={inlineLink} href={areaHref}>
          {areaName}
        </a>
        .
      </p>
    </div>
  );

  return (
    <ShowMoreContent
      controlsId="pricing-comparison-module-details"
      preview={preview}
      showMoreLabel={t('showMore')}
      showLessLabel={t('showLess')}
      showMoreAriaLabel={`${t('showMore')}: ${t('pricingComparisonTitle')}`}
      showLessAriaLabel={`${t('showLess')}: ${t('pricingComparisonTitle')}`}
    >
      <Card variant="default" size="md">
        <h3 id="pricing-comparison-averages-header" className={averagesCardHeader}>
          {t('pricingComparisonAreaAverageHeader', { home: plural })}
        </h3>
        <ul className={averagesList} aria-labelledby="pricing-comparison-averages-header">
          {display.areaEntries.map((stat) => (
            <li key={stat.id ?? stat.name} className={areaRow}>
              <a href={stat.link} className={areaRowLink}>
                {stat.name}
              </a>
              <span className={areaRowValue}>${formatWholeNumber(stat.areaAveragePrice)}</span>
            </li>
          ))}
        </ul>
      </Card>
    </ShowMoreContent>
  );
};
